function formatMoney(cents) {
  const whole = Math.floor(cents / 100);
  const decimal = cents % 100;
  if (decimal === 0) return `${whole},-`;
  const dec = decimal < 10 ? `0${decimal}` : `${decimal}`;
  return `${whole},${dec}`;
}

class CartDrawerRecommendations {
  constructor() {
    this.recommendationsContainer = null;
    this.cartDrawer = null;
    this.mutationObserver = null;
    this.isUpdating = false;
    this.swiperInstance = null;
    this.containerClickHandler = null;
    this.init();
  }

  init() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer) return;

    const isEnabled = cartDrawer.getAttribute('data-recommendations-enabled') !== 'false';
    if (!isEnabled) return;

    this.cartDrawer = cartDrawer;

    const observer = new MutationObserver(() => {
      const dialog = this.getDialog();
      if (dialog && dialog.open) {
        if (dialog.getAttribute('cart-summary-sticky') !== 'true') {
          dialog.setAttribute('cart-summary-sticky', 'true');
        }
        setTimeout(() => {
          const scrollContainer = dialog.querySelector('scroll-hint.cart-drawer__content');
          if (scrollContainer) scrollContainer.scrollTop = 0;
        }, 0);
        this.setupRecommendations();
        setTimeout(() => this.updateRecommendations(), 200);
      }
    });

    const dialog = this.getDialog();
    if (dialog) {
      observer.observe(dialog, { attributes: true });
      if (dialog.open) {
        this.setupRecommendations();
        setTimeout(() => this.updateRecommendations(), 200);
      }
    }

    document.addEventListener('cart:updated', () => {
      const dialog = this.getDialog();
      if (dialog && dialog.open) setTimeout(() => this.updateRecommendations(), 300);
    });

    document.addEventListener('cart-items-updated', () => {
      const dialog = this.getDialog();
      if (dialog && dialog.open) setTimeout(() => this.updateRecommendations(), 300);
    });
  }

  getDialog() {
    return this.cartDrawer?.querySelector('dialog');
  }

  async fetchProductData(productId) {
    try {
      const response = await fetch(`/products/${productId}.json`);
      if (!response.ok) return null;
      const data = await response.json();
      return data.product;
    } catch {
      return null;
    }
  }

  setupRecommendations() {
    const dialog = this.getDialog();
    if (!dialog) return;

    const container = dialog.querySelector('[data-recommendations-container="true"]');
    if (!container) return;

    this.recommendationsContainer = container;
    this.watchForRemovals(dialog);
    setTimeout(() => this.updateRecommendations(), 100);
  }

  watchForRemovals(dialog) {
    if (this.mutationObserver) this.mutationObserver.disconnect();

    this.mutationObserver = new MutationObserver(() => {
      if (this.isUpdating) return;
      if (this.recommendationsContainer && !document.contains(this.recommendationsContainer)) {
        this.setupRecommendations();
      }
    });

    // The container can live inside the sticky summary or inline under the line
    // items, so watch the whole content region to catch its removal either way.
    const content = dialog.querySelector('.cart-drawer__content') || dialog.querySelector('.cart-drawer__summary');
    if (content) {
      this.mutationObserver.observe(content, { childList: true, subtree: true });
    }
  }

  async updateRecommendations() {
    if (!this.recommendationsContainer || !document.contains(this.recommendationsContainer)) {
      this.setupRecommendations();
      return;
    }

    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      // Static mode: render the merchant-picked products, skip the recommendations API.
      if (this.recommendationsContainer.getAttribute('data-use-static') === 'true') {
        let staticProducts = [];
        try {
          staticProducts = JSON.parse(this.recommendationsContainer.getAttribute('data-static-products') || '[]');
        } catch {
          staticProducts = [];
        }
        if (staticProducts.length) {
          this.renderRecommendations(staticProducts);
        } else {
          this.recommendationsContainer.innerHTML = '';
        }
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (cart.items.length === 0) {
        this.recommendationsContainer.innerHTML = '';
        return;
      }

      const productId = cart.items[0].product_id;
      const recResponse = await fetch(`/recommendations/products.json?product_id=${productId}&limit=3`);
      if (!recResponse.ok) throw new Error(`API ${recResponse.status}`);

      const recommendations = await recResponse.json();

      if (recommendations.products && recommendations.products.length > 0) {
        const hasImages = recommendations.products.some(p => p.featured_image || p.image || p.featured_media);
        if (!hasImages) {
          const enriched = await Promise.all(recommendations.products.map(p => this.fetchProductData(p.id)));
          this.renderRecommendations(enriched.filter(Boolean));
        } else {
          this.renderRecommendations(recommendations.products);
        }
      } else {
        this.recommendationsContainer.innerHTML = '';
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      this.recommendationsContainer.innerHTML = '';
    } finally {
      this.isUpdating = false;
    }
  }

  renderRecommendations(products) {
    if (!this.recommendationsContainer) return;

    const heading = this.recommendationsContainer.getAttribute('data-heading') || 'You might also like';
    this.addButtonText = this.recommendationsContainer.getAttribute('data-add-button-text') || '+ add';
    this.collapsible = this.recommendationsContainer.getAttribute('data-collapsible') !== 'false';

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    // When collapsible, the header is a button with a chevron toggle; otherwise a plain heading.
    const headerHtml = this.collapsible
      ? `<button
          type="button"
          class="cart-drawer-recommendations__header"
          aria-expanded="true"
          aria-controls="cart-reco-carousel"
        >
          <h1 class="cart-drawer-recommendations__title">${heading}</h1>
          <span class="cart-drawer-recommendations__toggle" aria-hidden="true">
            <svg viewBox="0 0 12 8" width="12" height="8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 6.5L6 1.5L11 6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>`
      : `<div class="cart-drawer-recommendations__header">
          <h1 class="cart-drawer-recommendations__title">${heading}</h1>
        </div>`;

    const html = `
      <div class="cart-drawer-recommendations">
        ${headerHtml}
        <div class="cart-drawer-recommendations__carousel" id="cart-reco-carousel">
          <div class="swiper-container cart-reco-swiper">
            <div class="swiper-wrapper">
              ${products.map(product => this.renderProductCard(product)).join('')}
            </div>
            <div class="swiper-button-prev cart-reco-prev"></div>
            <div class="swiper-button-next cart-reco-next"></div>
            <div class="swiper-pagination cart-reco-pagination"></div>
          </div>
        </div>
      </div>
    `;

    this.recommendationsContainer.innerHTML = html;

    this.attachButtonListeners();
    this.attachToggleListener();
    this.initSwiper(); // delegation-based listener is already live; Swiper DOM changes won't break it
  }

  // Mobile-only collapse/expand of the recommendations carousel.
  attachToggleListener() {
    if (!this.collapsible) return; // feature disabled from the customizer
    const root = this.recommendationsContainer?.querySelector('.cart-drawer-recommendations');
    const header = root?.querySelector('.cart-drawer-recommendations__header');
    if (!root || !header) return;

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

    header.addEventListener('click', () => {
      if (!isMobile()) return; // desktop stays expanded
      const collapsed = root.classList.toggle('is-collapsed');
      header.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      // Swiper measures 0 width while hidden — refresh its layout on expand.
      if (!collapsed && this.swiperInstance) {
        this.swiperInstance.update();
      }
    });
  }

  initSwiper() {
    const swiperEl = this.recommendationsContainer?.querySelector('.cart-reco-swiper');
    if (!swiperEl) return;

    const init = () => {
      if (typeof Swiper === 'undefined') return;
      this.swiperInstance = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 4,
        grabCursor: true,
        autoHeight: true,
        breakpoints: {
          750: {
            slidesPerView: 1.5,
          },
        },
        navigation: {
          nextEl: swiperEl.querySelector('.cart-reco-next'),
          prevEl: swiperEl.querySelector('.cart-reco-prev'),
        },
        pagination: {
          el: swiperEl.querySelector('.cart-reco-pagination'),
          clickable: true,
        },
      });
    };

    // Small delay to ensure DOM and Swiper bundle are both ready
    setTimeout(() => {
      if (typeof Swiper !== 'undefined') {
        init();
      } else {
        let attempts = 0;
        const interval = setInterval(() => {
          if (typeof Swiper !== 'undefined') {
            clearInterval(interval);
            init();
          } else if (++attempts > 30) {
            clearInterval(interval);
          }
        }, 100);
      }
    }, 100);
  }

  renderProductCard(product) {
    let imageSrc = '';
    let imageAlt = '';

    // Prefer media objects — they carry the alt text that holds the
    // FULL_WIDTH / MODEL markers (the images/featured_image fields are bare URLs).
    const firstMedia = (product.media && product.media.length ? product.media[0] : null) || product.featured_media || null;
    if (firstMedia) {
      imageAlt = firstMedia.alt || '';
      const preview = firstMedia.preview_image || firstMedia;
      imageSrc = firstMedia.src || preview.src || preview.url || '';
    }

    if (!imageSrc) {
      if (product.images && product.images.length > 0) {
        const img = product.images[0];
        imageSrc = typeof img === 'string' ? img : (img.src || img.url || '');
        if (!imageAlt) imageAlt = (typeof img === 'object' ? img.alt : '') || '';
      } else if (product.featured_image) {
        imageSrc = typeof product.featured_image === 'string'
          ? product.featured_image
          : (product.featured_image.src || product.featured_image.url || '');
        if (!imageAlt && typeof product.featured_image === 'object') imageAlt = product.featured_image.alt || '';
      } else if (product.image) {
        imageSrc = typeof product.image === 'string' ? product.image : (product.image.src || product.image.url || '');
      }
    }

    if (!imageAlt) imageAlt = product.title;

    if (imageSrc) {
      if (imageSrc.startsWith('//')) imageSrc = 'https:' + imageSrc;
      if (imageSrc.startsWith('http') && !imageSrc.includes('?')) imageSrc += '?width=200';
    }

    // Mirror the cart line item cropping logic: detect FULL_WIDTH / MODEL markers
    // from the image filename or alt text (see snippets/cart-products.liquid).
    const rawName = imageSrc ? (imageSrc.split('?')[0].split('/').pop().split('__').pop().split('.')[0] || '') : '';
    const nameNorm = rawName.replace(/-/g, '_').toUpperCase();
    const altNorm = (imageAlt || '').replace(/-/g, '_').toUpperCase();
    const isFullWidth = nameNorm.includes('FULL_WIDTH') || altNorm.includes('FULL_WIDTH');
    const isModel = nameNorm.includes('MODEL') || altNorm.includes('MODEL');
    const imageWrapClass = `cart-reco-image-wrap${isFullWidth ? ' FULL_WIDTH' : ''}${isModel ? ' MODEL' : ''}`;

    const price = formatMoney(product.price);
    const comparePrice = product.compare_at_price && product.compare_at_price > product.price
      ? `<span class="cart-reco-price-compare">${formatMoney(product.compare_at_price)}</span>`
      : '';

    // Match the theme's `selected_or_first_available_variant`: prefer the first
    // in-stock variant. Falling back to variants[0] would try to add a sold-out
    // size, which /cart/add.js rejects (422) — the add would silently fail.
    let variant = null;
    if (Array.isArray(product.variants) && product.variants.length) {
      variant = product.variants.find(v => v.available) || product.variants[0];
    }
    const variantId = variant ? variant.id : product.id;
    const productUrl = product.url || `/products/${product.handle}`;

    return `
      <div class="swiper-slide cart-reco-slide">
        <div class="cart-reco-card-wrap">
          <a href="${productUrl}" class="cart-reco-image-link">
            <div class="${imageWrapClass}">
              ${imageSrc ? `<img class="cart-reco-image" src="${imageSrc}" alt="${imageAlt}" loading="lazy" onerror="this.style.display='none'">` : ''}
            </div>
          </a>
          <div class="cart-reco-info">
            <a href="${productUrl}" class="cart-reco-title-link">
              <p class="cart-reco-title">
                ${product.title}
                <br>
                <span class="cart-reco-price">${price}${comparePrice}</span>
              </p>
            </a>
            <span>
              <button
                class="cart-reco-add-btn"
                data-variant-id="${variantId}"
                type="button"
                aria-label="Add ${product.title} to cart"
              >
                ${this.addButtonText || '+ add'}
              </button>
            </span>
          </div>
        </div>
      </div>
    `;
  }

  attachButtonListeners() {
    if (!this.recommendationsContainer) return;

    // Remove previous listener before attaching — prevents stacking on re-renders
    if (this.containerClickHandler) {
      this.recommendationsContainer.removeEventListener('click', this.containerClickHandler);
    }

    this.containerClickHandler = (e) => {
      const button = e.target.closest('.cart-reco-add-btn');
      if (!button) return;
      e.preventDefault();
      e.stopPropagation();
      const variantId = button.getAttribute('data-variant-id');
      if (variantId) this.addToCart(variantId);
    };

    this.recommendationsContainer.addEventListener('click', this.containerClickHandler);
  }

  async addToCart(variantId) {
    if (!variantId) return;

    // Get the section ID the cart-items-component uses for re-rendering
    const cartItemsEl = document.querySelector('cart-items-component');
    const sectionId = cartItemsEl?.dataset.sectionId;

    const button = this.recommendationsContainer?.querySelector(`[data-variant-id="${variantId}"]`);
    if (button) {
      button.disabled = true;
      button.textContent = '...';
    }

    try {
      const body = { items: [{ id: Number(variantId), quantity: 1 }] };
      if (sectionId) body.sections = sectionId;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let detail = '';
        try {
          const err = await response.json();
          detail = err.description || err.message || '';
        } catch { /* non-JSON error body */ }
        throw new Error(`Cart add failed: ${response.status} ${detail}`.trim());
      }

      const data = await response.json();

      // Dispatch the theme's cart:update event — cart-items-component listens for this
      // and re-renders itself using data.sections if provided
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          data: {
            sections: data.sections || {},
            itemCount: data.item_count,
          },
        },
      }));

      // Update cart bubble count
      const cartBubbleCount = document.querySelector('cart-icon .cart-bubble__text-count');
      if (cartBubbleCount && data.item_count != null) {
        cartBubbleCount.textContent = data.item_count;
      }

      // Refresh recommendations after cart updates
      setTimeout(() => this.updateRecommendations(), 500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      if (button) {
        button.disabled = false;
        button.textContent = this.addButtonText || '+ add';
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawerRecommendations = new CartDrawerRecommendations();
  });
} else {
  window.cartDrawerRecommendations = new CartDrawerRecommendations();
}
